import { useCallback, useEffect, useState } from "react";

import { api } from "../api/cliente";
import type { RelogioNaTela } from "../api/tipos";
import { definirDeslocamento } from "../relogio";

/** De quanto em quanto tempo o relógio é relido: um avanço feito em outro navegador chega. */
const RELEITURA_MS = 60_000;

/**
 * O relógio da organização (`O-03`, ADR-059), lido uma vez pela moldura — e não pelo selo do
 * topo, que no celular só existe com o menu aberto: as contas de prazo de todas as telas
 * dependem do deslocamento desde a primeira tela.
 *
 * Só em demonstração (`ligado`): fora dela o deslocamento é zero e não há o que ler. Relido de
 * minuto em minuto, com a aba visível: o apresentador avança numa máquina, e a tela do operador,
 * em outra, passa a contar no tempo novo.
 */
export function useRelogioDaOrganizacao(ligado: boolean) {
  const [relogio, definirRelogio] = useState<RelogioNaTela | null>(null);

  useEffect(() => {
    if (!ligado) {
      definirRelogio(null);
      definirDeslocamento(0);
      return;
    }
    let vivo = true;
    const ler = () =>
      void api<RelogioNaTela>("/demonstracao/relogio")
        .then((r) => {
          if (!vivo) return;
          definirRelogio(r);
          definirDeslocamento(r.deslocamento_s);
        })
        // Uma leitura que falhou não muda o relógio: a próxima tenta.
        .catch(() => undefined);
    ler();
    const id = window.setInterval(() => {
      if (!document.hidden) ler();
    }, RELEITURA_MS);
    return () => {
      vivo = false;
      window.clearInterval(id);
    };
  }, [ligado]);

  /** Avança e devolve o relógio novo. Levanta o erro da API para quem chamou mostrar. */
  const avancar = useCallback(async (avanco: string) => {
    const r = await api<RelogioNaTela>("/demonstracao/relogio/avancar", {
      method: "POST",
      corpo: { avanco },
    });
    definirRelogio(r);
    // Avisa as telas (`definirDeslocamento`); o worker já foi acordado pela API e vence o que
    // venceu nos próximos segundos.
    definirDeslocamento(r.deslocamento_s);
    return r;
  }, []);

  return { relogio, avancar };
}
